import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';

// User operations
export const createUserProfile = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
      lastActivity: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error creating user profile:', error);
    return { success: false, error: error.message };
  }
};

export const updateUserActivity = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastActivity: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user activity:', error);
  }
};

// Search operations
export const createSearch = async (userId, searchData) => {
  try {
    // Get user's default fetch interval
    let defaultFetchInterval = 60; // fallback default
    try {
      const userPrefsResult = await getUserPreferences(userId);
      if (userPrefsResult.success) {
        defaultFetchInterval = userPrefsResult.preferences.defaultFetchInterval || 60;
      }
    } catch (error) {
      console.warn('Could not get user preferences for new search, using default:', error);
    }

    const searchRef = await addDoc(collection(db, 'searches'), {
      userId,
      ...searchData,
      createdAt: serverTimestamp(),
      lastScraped: null,
      listingCount: 0,
      newListingCount: 0,
      autoFetch: false,
      fetchInterval: defaultFetchInterval
    });
    return { success: true, id: searchRef.id };
  } catch (error) {
    console.error('Error creating search:', error);
    return { success: false, error: error.message };
  }
};

export const getUserSearches = async (userId) => {
  try {
    const q = query(
      collection(db, 'searches'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const searches = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, searches };
  } catch (error) {
    console.error('Error fetching user searches:', error);
    return { success: false, error: error.message };
  }
};

export const updateSearch = async (searchId, updateData) => {
  try {
    const searchRef = doc(db, 'searches', searchId);
    await updateDoc(searchRef, {
      ...updateData,
      lastScraped: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating search:', error);
    return { success: false, error: error.message };
  }
};

export const deleteSearch = async (searchId) => {
  try {
    // Delete all listings associated with this search
    const listingsQuery = query(
      collection(db, 'listings'),
      where('searchId', '==', searchId)
    );
    const listingsSnapshot = await getDocs(listingsQuery);

    const batch = writeBatch(db);
    listingsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete the search itself
    const searchRef = doc(db, 'searches', searchId);
    batch.delete(searchRef);

    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error('Error deleting search:', error);
    return { success: false, error: error.message };
  }
};

// Listing operations
export const createListings = async (searchId, userId, listingsData) => {
  try {
    const batch = writeBatch(db);
    const newListings = [];

    for (const listing of listingsData) {
      // Check if listing already exists
      const existingQuery = query(
        collection(db, 'listings'),
        where('annonceId', '==', listing.annonceId),
        where('searchId', '==', searchId)
      );
      const existingSnapshot = await getDocs(existingQuery);

      if (existingSnapshot.empty) {
        // Create new listing
        const listingRef = doc(collection(db, 'listings'));
        batch.set(listingRef, {
          searchId,
          userId,
          annonceId: listing.annonceId,
          title: listing.title,
          price: listing.price,
          year: listing.year,
          model: listing.model,
          location: listing.location,
          datePosted: listing.datePosted,
          firstSeen: serverTimestamp(),
          lastSeen: serverTimestamp(),
          isNew: true,
          image: listing.image,
          url: listing.url,
          details: listing
        });
        newListings.push(listing);
      } else {
        // Update existing listing's lastSeen
        const existingDoc = existingSnapshot.docs[0];
        batch.update(existingDoc.ref, {
          lastSeen: serverTimestamp(),
          details: listing
        });
      }
    }

    await batch.commit();

    // Update search counts
    if (newListings.length > 0) {
      const searchRef = doc(db, 'searches', searchId);
      const searchSnapshot = await getDoc(searchRef);
      const currentData = searchSnapshot.data();

      await updateDoc(searchRef, {
        listingCount: (currentData.listingCount || 0) + newListings.length,
        newListingCount: (currentData.newListingCount || 0) + newListings.length,
        lastScraped: serverTimestamp()
      });
    }

    return { success: true, newListingsCount: newListings.length };
  } catch (error) {
    console.error('Error creating listings:', error);
    return { success: false, error: error.message };
  }
};

export const getSearchListings = async (searchId, onlyNew = false) => {
  try {
    let q = query(
      collection(db, 'listings'),
      where('searchId', '==', searchId),
      orderBy('datePosted', 'desc')
    );

    if (onlyNew) {
      q = query(
        collection(db, 'listings'),
        where('searchId', '==', searchId),
        where('isNew', '==', true),
        orderBy('datePosted', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const listings = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, listings };
  } catch (error) {
    console.error('Error fetching search listings:', error);
    return { success: false, error: error.message };
  }
};

export const markListingsAsRead = async (searchId) => {
  try {
    const q = query(
      collection(db, 'listings'),
      where('searchId', '==', searchId),
      where('isNew', '==', true)
    );
    const querySnapshot = await getDocs(q);

    const batch = writeBatch(db);
    querySnapshot.docs.forEach(doc => {
      batch.update(doc.ref, { isNew: false });
    });

    // Reset search new listing count
    const searchRef = doc(db, 'searches', searchId);
    batch.update(searchRef, { newListingCount: 0 });

    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error('Error marking listings as read:', error);
    return { success: false, error: error.message };
  }
};

// Real-time listeners
export const subscribeToUserSearches = (userId, callback) => {
  const q = query(
    collection(db, 'searches'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (querySnapshot) => {
    const searches = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(searches);
  });
};

export const subscribeToSearchListings = (searchId, callback) => {
  const q = query(
    collection(db, 'listings'),
    where('searchId', '==', searchId),
    orderBy('datePosted', 'desc')
  );

  return onSnapshot(q, (querySnapshot) => {
    const listings = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(listings);
  });
};

// User preferences operations
export const getUserPreferences = async (userId) => {
  try {
    const userRef = doc(db, 'userPreferences', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return { success: true, preferences: userDoc.data() };
    } else {
      // Return default preferences
      const defaultPreferences = {
        defaultFetchInterval: 60, // 60 minutes
        enableNotifications: true
      };
      return { success: true, preferences: defaultPreferences };
    }
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    return { success: false, error: error.message };
  }
};

export const updateUserPreferences = async (userId, preferences) => {
  try {
    const userRef = doc(db, 'userPreferences', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      await updateDoc(userRef, {
        ...preferences,
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(userRef, {
        ...preferences,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    return { success: true };
  } catch (error) {
    console.error('Error updating user preferences:', error);
    return { success: false, error: error.message };
  }
};

// Auto-fetch operations
export const updateSearchAutoFetch = async (searchId, autoFetch, fetchInterval) => {
  try {
    const searchRef = doc(db, 'searches', searchId);
    await updateDoc(searchRef, {
      autoFetch,
      fetchInterval,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating search auto-fetch:', error);
    return { success: false, error: error.message };
  }
};

// Synchronize search intervals with user preferences
export const synchronizeSearchIntervals = async (userId, defaultFetchInterval) => {
  try {
    // Get all user's searches
    const q = query(
      collection(db, 'searches'),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { success: true, updatedCount: 0 };
    }

    // Update all searches with the new default interval
    const batch = writeBatch(db);
    let updatedCount = 0;

    querySnapshot.docs.forEach(docSnapshot => {
      const searchRef = doc(db, 'searches', docSnapshot.id);
      batch.update(searchRef, {
        fetchInterval: defaultFetchInterval,
        updatedAt: serverTimestamp()
      });
      updatedCount++;
    });

    await batch.commit();
    return { success: true, updatedCount };
  } catch (error) {
    console.error('Error synchronizing search intervals:', error);
    return { success: false, error: error.message };
  }
};

// Update search settings via backend API (includes scheduler integration)
export const updateSearchSettings = async (searchId, settings) => {
  try {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const response = await fetch(`${BACKEND_URL}/api/searches/${searchId}/update-settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update search settings');
    }

    const result = await response.json();
    return { success: true, message: result.message };
  } catch (error) {
    console.error('Error updating search settings via backend:', error);
    return { success: false, error: error.message };
  }
};